/**
 * Name search component that allows users to search for and select contacts by name.
 *
 * This component provides a comprehensive contact search interface with the following features:
 * - Real-time search with debouncing to reduce API calls
 * - Click-to-select functionality for easy contact selection
 * - Selected names management with a dedicated modal
 * - Integrates seamlessly with outreach filter context
 * - Outside click handling to close search results
 * - Error handling and user feedback for failed searches
 *
 * @fileoverview NameSearchController - Contact search and selection component for outreach filtering
 * @version 1.0.0
 */

import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { debounce, Button, useOpenClose } from '@evertrue/sdk';
import { Modal, ModalHeader, ModalBody, Icon } from '@evertrue/et-components';
import GlobalSearchBox from 'apps/layout/global-search/components/global-search-box';
import useOutsideClick from 'hooks/use-outside-click';
import { useGlobalSearch } from 'apps/layout/global-search/global-search-context';
import { getQueryTemplate } from 'apps/layout/global-search/config/global-search-config';
import Api from 'entities/helpers/api';
import EverTrue from 'app';
import { useOutreachFilterProvider } from '../../../contexts/outreach-filter-context';
import colors from 'base/colors';
import ErrorLogger from 'entities/helpers/error-log-helper';
import Decorator from 'clientDecorator';

// Constants
const SEARCH_DEBOUNCE_DELAY = 500;
const MAX_SEARCH_RESULTS = 50;

/**
 * Contact data structure used throughout the component
 * @typedef {Object} Contact
 * @property {string} contactId - Unique contact identifier
 * @property {string} name - Contact's display name
 * @property {string[]} emailAddress - Array of contact's email addresses
 */

/**
 * NameSearchController component for searching and selecting contacts by name
 *
 * This component manages the complete lifecycle of contact search and selection:
 * - Provides a search input with real-time results
 * - Handles contact selection and maintains selected state
 * - Offers a modal interface for managing selected contacts
 * - Integrates with the outreach filter system
 *
 * @param {Object} props - Component props
 * @param {string} [props.className=''] - Additional CSS class name for styling customization
 * @param {Function} [props.onNameSelect] - Callback function called when a name is selected
 * @returns {JSX.Element} The rendered NameSearchController component
 */
const NameSearchController = ({ className = '', onNameSelect }) => {
  // Refs
  const searchBoxRef = useRef(null);

  // Local state for search functionality
  const [isFocused, setIsFocused] = useState(false);
  const [nameSearchResults, setNameSearchResults] = useState([]);
  const [nameSearchLoading, setNameSearchLoading] = useState(false);

  // Global state from context providers
  const { setSelectedNames, selectedNames } = useOutreachFilterProvider();
  const { query, setQuery, clearSearch } = useGlobalSearch();

  // Modal state management
  const [isOpen, openModal, closeModal] = useOpenClose();

  /**
   * Clears the search input box programmatically
   * Triggers a change event to ensure all related state is updated
   */
  const clearSearchBox = () => {
    if (searchBoxRef.current) {
      searchBoxRef.current.value = '';
      const event = new Event('change', { bubbles: true });
      searchBoxRef.current.dispatchEvent(event);
    }
  };

  /**
   * Handles clicks outside the search component
   * Clears search state and results when user clicks elsewhere (only if not focused)
   */
  const handleOutsideClick = () => {
    if (!isFocused) {
      clearSearch();
      clearSearchBox();
      setNameSearchResults([]);
    }
  };

  // Set up outside click detection
  useOutsideClick(searchBoxRef, handleOutsideClick);

  /**
   * Removes a contact from the selected names list and triggers refresh
   * @param {Contact} contactToRemove - Contact to remove from the selection
   * @param {string} contactToRemove.contactId - Unique contact identifier
   */
  const handleRemoveName = (contactToRemove) => {
    const newSelectedNames = selectedNames.filter((contact) => contact.contactId !== contactToRemove.contactId);
    setSelectedNames(newSelectedNames);
    // Trigger immediate refresh
    onNameSelect?.();
  };

  /**
   * Transforms a raw contact object from the API into the standardized Contact format
   * @param {Object} rawContact - Raw contact data from API
   * @returns {Contact|null} Transformed contact object or null if invalid
   */
  const transformContact = (rawContact) => {
    const parsedContact = Decorator.Contacts.parse(rawContact);
    if (!parsedContact?.name) {
      return null;
    }

    const emailIdentities = rawContact.identities?.filter((identity) => identity.type === 'email') || [];
    const emailAddresses = emailIdentities.map((identity) => identity.value);
    const remoteId = rawContact.identities?.find((identity) => identity.type === 'remote_id')?.value || 'N/A';

    return {
      contactId: rawContact.id, // Fixed: use 'id' consistently throughout the component
      name: parsedContact.name,
      emailAddress: emailAddresses,
      remoteId: remoteId,
    };
  };

  /**
   * Handles contact search via API with debouncing and error handling
   * Searches for contacts by name and updates the search results state
   *
   * @param {string} searchQuery - The search term entered by user
   * @returns {Promise<void>} Promise that resolves when search completes
   */
  const handleSearch = async (searchQuery) => {
    const trimmedQuery = searchQuery.trim();

    // Clear results if no search query
    if (!trimmedQuery) {
      setNameSearchResults([]);
      return;
    }

    setNameSearchLoading(true);

    try {
      const contactResponse = await Api.CONTACTS.SEARCH.post({
        data: JSON.stringify(getQueryTemplate(trimmedQuery, 'name')),
        params: { limit: MAX_SEARCH_RESULTS },
      });

      // Transform and filter valid contacts
      const results = (contactResponse.items || []).map(transformContact).filter(Boolean); // Remove null values

      setNameSearchResults(results);
    } catch (error) {
      ErrorLogger.error('Error in name search', {
        extra: {
          error,
          searchQuery: trimmedQuery,
          page_name: 'outreach name search',
        },
      });
      EverTrue.Alert.error('Unable to search for contacts. Please try again.');
      setNameSearchResults([]);
    } finally {
      setNameSearchLoading(false);
    }
  };

  /**
   * Handles clicking on a search result to select a contact
   * Adds the contact to the selected names list and prevents duplicates
   *
   * @param {Contact} contact - The contact to add to selection
   */
  const handleNameClick = (contact) => {
    // Check if contact is already selected to prevent duplicates
    const isAlreadySelected = selectedNames.some((selected) => selected.contactId === contact.contactId);

    if (!isAlreadySelected) {
      setSelectedNames((prev) => [...prev, contact]);

      // Reset pagination when a new name filter is added
      onNameSelect?.();

      // Clear search after selection for better UX
      clearSearch();
      clearSearchBox();
      setNameSearchResults([]);
    }
  };

  return (
    <>
      {/* Main search input container */}
      <div className="email-search-container">
        {/* Selected names summary button - only shown when contacts are selected */}
        <Button type="primary" onClick={openModal} disabled={selectedNames.length === 0} style={{ padding: '0px' }}>
          <Icon icon="members" size={1} />
          {selectedNames.length}
        </Button>

        <GlobalSearchBox
          searchRef={searchBoxRef}
          onChange={debounce((newQuery = '') => {
            setQuery(newQuery);
            handleSearch(newQuery);
          }, SEARCH_DEBOUNCE_DELAY)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder="Name Search..."
          className="outreach-filter-search-box"
        />

        {/* Search results dropdown - only shown when there's a query and results/loading */}
        {query && (nameSearchResults.length > 0 || nameSearchLoading) && (
          <div className="email-search-dropdown">
            <ul className="email-search--list">
              {/* Loading state */}
              {nameSearchLoading && <li className="email-search--loading">Searching...</li>}

              {/* Empty results state */}
              {!nameSearchLoading && nameSearchResults.length === 0 && (
                <li className="email-search--empty">No names found</li>
              )}

              {/* Search results */}
              {!nameSearchLoading &&
                nameSearchResults.length > 0 &&
                nameSearchResults.map((contact) => {
                  const isSelected = selectedNames.some((selected) => selected.contactId === contact.contactId);

                  return (
                    <li key={contact.contactId} className="email-search--item">
                      <div
                        className={classNames('email-search-result-card', {
                          'is-selected': isSelected,
                        })}
                        onClick={() => handleNameClick(contact)}
                      >
                        <div className="email-address">{contact.name}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
                          <span className="email-address">Remote ID:</span>
                          <span style={{ wordBreak: 'break-word', flex: '1 1 auto' }}>{contact.remoteId}</span>
                        </div>
                        {isSelected && <Icon icon="check" size={1} />}
                      </div>
                    </li>
                  );
                })}
            </ul>
          </div>
        )}
      </div>

      {/* Selected names management modal */}
      <Modal isOpen={isOpen} closeModal={closeModal} size="small" closeOnOutsideClick={true}>
        <ModalHeader title="Selected Name Filters" />
        <ModalBody>
          These names are currently applied as filters. Remove any you no longer want included in your results.
        </ModalBody>

        {/* Selected contacts list with remove functionality */}
        <div className="edit-selected-addresses-modal">
          {selectedNames.map((contact) => (
            <div className="email-card" key={contact.contactId}>
              <div className="contact-name">{contact.name}</div>
              <Button
                onClick={() => handleRemoveName(contact)}
                type="warning"
                title={`Remove ${contact.name} from filters`}
              >
                <Icon icon="trash" size={1} style={{ color: colors.red500 }} />
              </Button>
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
};

// PropTypes for type checking and documentation
NameSearchController.propTypes = {
  /** Additional CSS class name for styling customization */
  className: PropTypes.string,
  /** Callback function called when a name is selected */
  onNameSelect: PropTypes.func,
};

// Default props
NameSearchController.defaultProps = {
  className: '',
  onNameSelect: null,
};

export default NameSearchController;
